import { useMemo } from 'react';
import { cloneDeep } from 'lodash-es';
import {
  useQuantzData,
  QuantzQueryCacheDuration,
  QuantzOperation,
  QuantzEventType,
} from '@hotjar/quantz';
import { URLConverter, QuerySubKeyEnum } from '@hotjar/global-filters';

import type { QuantzResponse } from '@hotjar/quantz';
import type { GlobalFilter } from '@hotjar/global-filters';

import {
  mapDeviceToGlobalFilter,
  mapTargetingValueToRadioTextFilterOperation,
} from '../lib/mapHeatmapParamsToGlobalFilters';
import type { SessionErrorsInsightsHookParams } from '../constants-types/SessionErrors';

/*
 Since we want to provide an arbitrary cache time we force the enabled state.
 At the moment we don't have external factors that can resolve it to false.
 */
const ENABLED = true;

export function useGetSessionErrors({
  selectedDateRange,
  selectedTargetingValue,
  selectedDeviceType,
  searchPattern,
  siteId,
  selectedFiltersInUiFormat,
}: SessionErrorsInsightsHookParams): {
  isLoading: boolean;
  isError: boolean;
  isEmpty: boolean;
  data: QuantzResponse | undefined;
} {
  const { filterList } = selectedFiltersInUiFormat;
  const tempSearchOperand = URLConverter.convertUiOperation(
    mapTargetingValueToRadioTextFilterOperation(selectedTargetingValue),
  )?.operation;
  const searchOperand = tempSearchOperand ? tempSearchOperand.toString() : 'SIMPLE_MATCH';

  const sessionFilters: GlobalFilter = useMemo(
    () => ({
      dateRange: selectedDateRange,
      filterList: [...cloneDeep(filterList), mapDeviceToGlobalFilter(selectedDeviceType)],
    }),
    [selectedDateRange, selectedDeviceType, filterList],
  );

  const { isLoading, isError, data } = useQuantzData(
    {
      sessionFilters,
      siteId,
      operation: QuantzOperation.COUNT,
      eventType: QuantzEventType.ERROR,
      eventFilters: {
        [searchOperand]: { [QuerySubKeyEnum.page_path]: searchPattern },
      },
      windowed: true,
    },
    ENABLED,
    QuantzQueryCacheDuration.Mid,
  );

  const isEmpty =
    data == null ||
    data?.data?.resultSeries == null ||
    data.data.resultSeries[0] === undefined ||
    data?.data?.resultSeries?.[0].values.length === 0 ||
    data?.data?.total === 0 ||
    data?.data?.total === undefined;

  return { isLoading, isError, isEmpty, data };
}
